import { IconTag } from '@tabler/icons-react';
import { SearchTagsRequestQuery, SearchTagsResponseBody } from 'bff';
import { useCallback, useState, forwardRef, useImperativeHandle } from 'react';
import {
  Badge,
  Button,
  ComboboxCheckbox,
  ComboboxOption,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  cn,
} from 'ui';
import { FileList, FileListItem, FileListLabel } from './File';
import { FileInput, FileInputProps, FileLabelBadge } from './FileInput';
import { useFilePreviews } from '../contexts/FilePreviewsContext';
// @ts-ignore
import { nanoid } from 'nanoid';
import { IconTrash } from '@tabler/icons-react';

const TWENTY_MB_IN_BYTES = 20 * 1024 * 1024;

export interface FileUploadResponse {
  token: string;
  url: string;
}

export interface FileMeta {
  name: string;
  url: string;
}

export interface FileExtended {
  id: string;
  tags: ComboboxOption[];
  progress?: number;
  isLoading?: boolean;
  name: string;
  size: number;
  token?: string;
  preview?: string;
}

export interface UploadFileProps
  extends Pick<
    FileInputProps,
    'fileTypes' | 'maxFileSize' | 'onRejectedFiles'
  > {
  render?: {
    submit?: React.FC;
  };
  searchTags?: (
    query: SearchTagsRequestQuery,
    cookie?: string,
  ) => Promise<SearchTagsResponseBody>;
  includeTagger?: boolean;
  children?: React.ReactNode;
  defaultValue?: FileExtended[];
  invalid?: boolean;
  maxFiles?: number;
  onRemoveFile: (index: number) => void;
  onUploadFile: (
    file: File,
    notifyProgress: (progress: number) => void,
  ) => Promise<FileUploadResponse>;
  onUploadError?: (file: File) => void;
  onUpdateFile?: (index: number, file: Partial<FileExtended>) => void;
  maxTagsPerFile?: number;
  disabled?: boolean;
  label?: string;
  docKey?: string;
  uploadMessage?: string;
  className?: string;
  fromCreateClaim?: boolean;
  showUploadButton?: boolean;
  onSubmit?: () => void;
  isSubmitLoading?: boolean;
  submitButtonText?: string;
}

export interface UploadFileRef {
  reset: () => void;
}

export const UploadFile = forwardRef<UploadFileRef, UploadFileProps>(
  (
    {
      searchTags,
      includeTagger = false,
      fileTypes,
      maxFileSize = TWENTY_MB_IN_BYTES,
      onUploadFile,
      onUploadError,
      maxTagsPerFile = 2,
      onRemoveFile,
      onRejectedFiles,
      maxFiles = 1,
      invalid,
      defaultValue = [],
      children,
      onUpdateFile,
      disabled,
      label,
      docKey,
      uploadMessage,
      className,
      fromCreateClaim,
      showUploadButton = false,
      onSubmit,
      isSubmitLoading = false,
      submitButtonText = 'Cargar documento',
      render,
    },
    ref,
  ) => {
    const { rememberPreview, getPreview } = useFilePreviews();

    const [files, setFiles] = useState<FileExtended[]>(defaultValue);

    useImperativeHandle(ref, () => ({
      reset: () => setFiles([]),
    }));

    const updateFile = useCallback(
      (
        targetId: string,
        modifier: (file: FileExtended) => Partial<FileExtended>,
      ) => {
        setFiles((files) =>
          files.map((file) => {
            if (file.id === targetId) {
              return {
                ...file,
                ...modifier(file),
              };
            }

            return file;
          }),
        );
      },
      [],
    );

    const handleUploadFile = useCallback(
      (file: File) => {
        const id = nanoid();

        const preview = file.type.startsWith('image/')
          ? URL.createObjectURL(file)
          : undefined;

        setFiles((files) =>
          files.concat([
            {
              name: file.name,
              size: file.size,
              progress: 0,
              isLoading: true,
              tags: [],
              preview,
              id,
            },
          ]),
        );

        onUploadFile(file, (progress) => {
          updateFile(id, () => ({
            progress,
          }));
        })
          .then(({ token }) => {
            if (preview) {
              rememberPreview(token, preview);
            }

            updateFile(id, () => ({
              isLoading: false,
              token,
            }));
          })
          .catch(() => {
            // The upload failed: drop the file so the list never shows a
            // file that is not actually stored in cloud storage.
            setFiles((files) => files.filter((f) => f.id !== id));

            if (preview) {
              URL.revokeObjectURL(preview);
            }

            if (onUploadError) {
              onUploadError(file);
            }
          });
      },
      [setFiles, files, onUploadError, rememberPreview],
    );

    const stopPropagation = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
    };

    const [firstFile] = files;

    return (
      <div className='relative'>
        <FileInput
          preview={
            firstFile?.preview ||
            (firstFile?.token ? getPreview(firstFile.token) : undefined)
          }
          invalid={invalid}
          maxFiles={maxFiles}
          disabled={files.length === maxFiles}
          onDropFiles={(files) => {
            for (const file of files) {
              handleUploadFile(file);
            }
          }}
          onRejectedFiles={onRejectedFiles}
          fileTypes={fileTypes}
          maxFileSize={maxFileSize}
          label={label}
          docKey={docKey}
          isFileLoaded={files.length > 0}
          className={className}
          uploadMessage={uploadMessage}
          fromCreateClaim={fromCreateClaim}
        >
          {files.length > 0 && (
            <FileList className='overflow-y-auto pointer-events-auto z-10 cursor-auto items-center flex flex-col justify-center space-y-1'>
              {label && <FileLabelBadge>{label}</FileLabelBadge>}

              {files.map(({ name, isLoading, tags }, index) => {
                return (
                  <FileListItem
                    key={index}
                    className='border-0 h-6 text-[#B7B7C0] space-x-1 justify-center'
                  >
                    <div className='[&_svg:nth-child(1)]:text-primary [&_svg:nth-child(1)]:!mr-1'>
                      <FileListLabel
                        withDefaultIcon
                        withMimetypeIcon={false}
                        className='[&>p]:!text-2xs text-stone-700 !mr-0'
                      >
                        {name}
                      </FileListLabel>
                    </div>

                    {isLoading ? (
                      <Spinner size={'xs'} />
                    ) : (
                      <div>
                        <Button
                          onClick={(e) => {
                            stopPropagation(e);
                            setFiles((files) =>
                              files.filter((_, i) => i !== index),
                            );
                            onRemoveFile(index);
                          }}
                          disabled={disabled}
                          size='xs'
                          className={cn(
                            'ml-1 rounded-l-none rounded-tr-none p-0 h-auto',
                            {
                              'px-3': includeTagger,
                            },
                            {
                              'pointer-events-auto': !disabled,
                            },
                          )}
                          variant={'ghost'}
                          type='button'
                        >
                          <IconTrash className='w-3 h-3' />
                        </Button>
                        {includeTagger && !!searchTags && (
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                className={cn(
                                  'rounded-l-none rounded-tr-none p-0 px-1',
                                  {
                                    'px-3': includeTagger,
                                  },
                                )}
                                variant={'ghost'}
                                type='button'
                                onClick={stopPropagation}
                              >
                                <IconTag className='w-2 h-2' />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              className='w-[200px] p-0'
                              align='start'
                            >
                              <ComboboxCheckbox
                                value={tags}
                                onClose={() => {}}
                                render={({ label }) => <>{label}</>}
                                placeholder='Buscar etiquetas'
                                onSelect={({ value, label }) => {
                                  const shouldRemove = tags.some(
                                    (ele) => ele.value === value,
                                  );

                                  const tagsUpdated = shouldRemove
                                    ? tags.filter(
                                        (tag) =>
                                          tag.value.toString() !==
                                          value.toString(),
                                      )
                                    : maxTagsPerFile <= tags.length
                                    ? tags
                                    : [...tags, { label, value }];

                                  if (onUpdateFile) {
                                    onUpdateFile(index, { tags: tagsUpdated });
                                  }

                                  setFiles((prevFiles) => {
                                    return prevFiles.map((file, iterIndex) => {
                                      if (iterIndex !== index) return file;

                                      return {
                                        ...file,
                                        tags: tagsUpdated,
                                      };
                                    });
                                  });
                                }}
                                loadOptions={async (search) =>
                                  searchTags &&
                                  searchTags({ search, limit: 10 }).then(
                                    (res) =>
                                      res.tags.map((t) => {
                                        return {
                                          label: t.name,
                                          value: t.id.toString(),
                                        };
                                      }),
                                  )
                                }
                                defaultOptions={tags}
                                emptyStateMessage=''
                              />
                            </PopoverContent>
                          </Popover>
                        )}
                      </div>
                    )}
                  </FileListItem>
                );
              })}

              {!!render?.submit && render.submit()}
            </FileList>
          )}
        </FileInput>

        {children}

        {showUploadButton && files.length > 0 && (
          <div className='bottom-4 right-4 z-50' onClick={stopPropagation}>
            <Button
              disabled={isSubmitLoading || disabled}
              loading={isSubmitLoading}
              onClick={(e) => {
                stopPropagation(e);
                if (onSubmit) {
                  onSubmit();
                }
              }}
              type='button'
              size='xs'
              className='text-base_brand h-8 py-2 px-3 font-medium text-white bg-primary'
            >
              {submitButtonText}
            </Button>
          </div>
        )}
      </div>
    );
  },
);
